/**
 * Lead-passenger email verification state (session-scoped).
 *
 * The lead traveler's email is prefilled with the logged-in account email. If the
 * user changes it to a *different* email, that email must be OTP-verified before
 * they can proceed. This tiny store lets the (React) verify affordance and the
 * (pure, non-React) Next-gate validators agree on what's been verified without
 * threading state through every validator signature.
 *
 * - `enforce` becomes true once an account email is known (i.e. the user is logged
 *   in). For a guest with no account email, verification is NOT enforced.
 * - `verified` holds the account email (implicitly trusted) plus any OTP-verified
 *   emails, all lowercased/trimmed.
 * - Session-scoped on purpose: re-verify a changed email once per session.
 */

const KEY = "_zenvoya_lead_email_verification";

/**
 * sessionStorage key recording which account email the LEAD traveler email was
 * auto-set to this session. Lets the prefill overwrite a stale cross-session value
 * (e.g. a different account's email persisted in `_traveler_info_array`) exactly
 * once per session, then respect in-session edits.
 */
export const LEAD_EMAIL_PREFILL_SESSION_KEY = "_zenvoya_lead_email_prefilled_for";

interface State {
  enforce: boolean;
  verified: string[];
}

const norm = (email: string | undefined | null): string => String(email ?? "").trim().toLowerCase();

const read = (): State => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return { enforce: false, verified: [] };
    const parsed = JSON.parse(raw);
    return {
      enforce: !!parsed?.enforce,
      verified: Array.isArray(parsed?.verified) ? parsed.verified : [],
    };
  } catch {
    return { enforce: false, verified: [] };
  }
};

const write = (state: State): void => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* sessionStorage unavailable — verification simply won't persist within the session */
  }
};

/**
 * Record the logged-in account email. Turns enforcement on and marks that email as
 * trusted (no verification needed for the account's own address). Call on mount of
 * the lead-email verify affordance. No-op for an empty email (guest).
 */
export const initAccountEmail = (accountEmail: string | undefined | null, enabled = true): void => {
  const email = norm(accountEmail);
  const state = read();
  // Feature flag off (ENABLE_EMAIL_VERIFICATION) or no logged-in account email
  // (guest) → do not enforce, so nothing is ever hard-blocked. Keeps enforcement in
  // sync when the flag is toggled or the user logs out mid-session.
  if (!enabled || !email) {
    if (state.enforce) write({ ...state, enforce: false });
    return;
  }
  const verified = state.verified.includes(email) ? state.verified : [...state.verified, email];
  write({ enforce: true, verified });
};

export const markEmailVerified = (email: string | undefined | null): void => {
  const value = norm(email);
  if (!value) return;
  const state = read();
  if (state.verified.includes(value)) return;
  write({ ...state, verified: [...state.verified, value] });
};

export const isEmailVerified = (email: string | undefined | null): boolean => {
  const value = norm(email);
  if (!value) return false;
  return read().verified.includes(value);
};

/**
 * Whether the lead affordance should ask the user to verify: an account email is
 * known, the entered email is non-empty, differs from the account email, and hasn't
 * been verified yet.
 */
export const leadEmailNeedsVerification = (
  email: string | undefined | null,
  accountEmail: string | undefined | null
): boolean => {
  const value = norm(email);
  const account = norm(accountEmail);
  if (!account || !value) return false;
  if (value === account) return false;
  return !isEmailVerified(value);
};

/**
 * Next-gate check (used by the pure validators). Passes when: enforcement is off
 * (guest / not logged in), the email is empty (handled by the separate required
 * check), or the email is verified/trusted. Blocks a changed-but-unverified lead
 * email.
 */
export const isLeadEmailOk = (email: string | undefined | null): boolean => {
  const state = read();
  if (!state.enforce) return true;
  const value = norm(email);
  if (!value) return true;
  return state.verified.includes(value);
};
